import { useState, useEffect } from 'react';
import { getSettings, saveSettings, getFinancials } from '../api';
import { colours as c, font, gbp, num } from '../theme';

const Skeleton = ({ height = '20px' }) => (
  <div className="skeleton" style={{ width: '100%', height }} />
);

// Never send "" to a numeric column — that's what broke saves before.
const numOrZero = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export default function AdminSettings() {
  const year = new Date().getFullYear();
  const blank = {
    year,
    subscription_amount: 36,
    first_prize: 0,
    second_prize: 0,
    third_prize: 0,
    use_percentages: true,
    first_prize_percent: 40,
    second_prize_percent: 25,
    third_prize_percent: 15,
    max_entries_per_member: 5,
    allow_multiple_wins: false,
  };

  const [form, setForm] = useState(blank);
  const [saved, setSaved] = useState(blank);
  const [potMembers, setPotMembers] = useState(0);
  const [potEntries, setPotEntries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([getSettings(year), getFinancials()])
      .then(([sRes, fRes]) => {
        if (sRes.data.settings) {
          const merged = { ...blank, ...sRes.data.settings };
          setForm(merged);
          setSaved(merged);
        }
        setPotMembers(fRes.data.activeMemberCount || 0);
        // Pot preview follows entries, not heads. Older API responses have no
        // activeEntryCount, so fall back to the member count (one each).
        setPotEntries(fRes.data.activeEntryCount ?? fRes.data.activeMemberCount ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => {
    const v = e.target.type === 'number' ? e.target.value : e.target.value;
    setForm({ ...form, [key]: v });
  };

  const monthlyRate = numOrZero(form.subscription_amount) / 12;
  const pot = potEntries * monthlyRate;

  const p1 = numOrZero(form.first_prize_percent);
  const p2 = numOrZero(form.second_prize_percent);
  const p3 = numOrZero(form.third_prize_percent);
  const retainedPct = 100 - p1 - p2 - p3;
  const overAllocated = retainedPct < 0;

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const handleSave = async () => {
    if (overAllocated) {
      setMessage('The three prizes add up to more than 100% of the pot.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        year: parseInt(form.year, 10) || year,
        subscription_amount: numOrZero(form.subscription_amount),
        first_prize: numOrZero(form.first_prize),
        second_prize: numOrZero(form.second_prize),
        third_prize: numOrZero(form.third_prize),
        use_percentages: !!form.use_percentages,
        first_prize_percent: p1,
        second_prize_percent: p2,
        third_prize_percent: p3,
        max_entries_per_member: Math.max(1, parseInt(form.max_entries_per_member, 10) || 1),
        allow_multiple_wins: !!form.allow_multiple_wins,
      };
      await saveSettings(payload);
      setSaved(payload);
      setForm(payload);
      setMessage('Settings saved. They apply from the next draw onwards.');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Settings could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Skeleton height="260px" />
      </div>
    );
  }

  const prizeRow = (label, key, amount) => (
    <div style={S.prizeRow} key={key}>
      <span style={S.prizeLabel}>{label}</span>
      <div style={S.inputGroup}>
        <input
          type="number"
          value={form[key]}
          onChange={set(key)}
          style={S.inputBare}
        />
        <span style={S.suffix}>{form.use_percentages ? '%' : '£'}</span>
      </div>
      {form.use_percentages && (
        <span style={S.prizeAmount}>{gbp(amount)}</span>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      <span style={S.intro}>
        Changes apply to the next draw, never to draws already made
      </span>

      {message && (
        <div
          style={{
            ...S.message,
            background: message.includes('saved') ? c.activeBg : c.lapsedBg,
            color: message.includes('saved') ? c.activeFg : c.lapsedFg,
          }}
          onClick={() => setMessage('')}
        >
          {message}
        </div>
      )}

      <div style={S.grid} className="grid-2">

        {/* the basics */}
        <div style={S.card}>
          <span style={S.cardTitle}>The basics</span>

          <label style={S.field}>
            <span style={S.label}>Club year</span>
            <input type="number" value={form.year} onChange={set('year')} style={S.input} />
          </label>

          <label style={S.field}>
            <span style={S.label}>Annual subscription</span>
            <div style={S.inputGroup}>
              <span style={S.prefix}>£</span>
              <input
                type="number"
                value={form.subscription_amount}
                onChange={set('subscription_amount')}
                style={S.inputBare}
              />
            </div>
            <span style={S.hint}>
              {gbp(monthlyRate)} a month · monthly members pay this rate directly
            </span>
          </label>

          <div style={S.potBox}>
            <span style={S.hint}>
              With {num(potEntries)} entr{potEntries === 1 ? 'y' : 'ies'} held
              across {num(potMembers)} member{potMembers === 1 ? '' : 's'}, that makes a pot of
            </span>
            <span style={S.potFigure}>{gbp(pot)} a month</span>
          </div>

          <label style={S.field}>
            <span style={S.label}>Most entries one member can hold</span>
            <input
              type="number"
              min="1"
              value={form.max_entries_per_member}
              onChange={set('max_entries_per_member')}
              style={S.input}
            />
            <span style={S.hint}>
              Each entry is {gbp(monthlyRate)} a month and a separate chance in
              the draw. Lowering this does not remove entries members already hold.
            </span>
          </label>

          <label style={S.field}>
            <span style={S.label}>Can one member win more than one prize?</span>
            <div style={S.toggle}>
              <button
                type="button"
                style={!form.allow_multiple_wins ? S.toggleOn : S.toggleOff}
                onClick={() => setForm({ ...form, allow_multiple_wins: false })}
              >
                One prize each
              </button>
              <button
                type="button"
                style={form.allow_multiple_wins ? S.toggleOn : S.toggleOff}
                onClick={() => setForm({ ...form, allow_multiple_wins: true })}
              >
                Every entry counts
              </button>
            </div>
            <span style={S.hint}>
              {form.allow_multiple_wins
                ? 'Each entry is drawn independently, so a member holding several could take two prizes in the same month.'
                : 'Once a member wins, their remaining entries come out of the hat before the next prize is drawn.'}
            </span>
          </label>
        </div>

        {/* prizes */}
        <div style={S.card}>
          <span style={S.cardTitle}>How prizes are worked out</span>

          <div style={S.toggle}>
            <button
              style={!form.use_percentages ? S.toggleOn : S.toggleOff}
              onClick={() => setForm({ ...form, use_percentages: false })}
            >
              Fixed amounts
            </button>
            <button
              style={form.use_percentages ? S.toggleOn : S.toggleOff}
              onClick={() => setForm({ ...form, use_percentages: true })}
            >
              Share of the pot
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            {form.use_percentages ? (
              <>
                {prizeRow('1st', 'first_prize_percent', pot * p1 / 100)}
                {prizeRow('2nd', 'second_prize_percent', pot * p2 / 100)}
                {prizeRow('3rd', 'third_prize_percent', pot * p3 / 100)}
              </>
            ) : (
              <>
                {prizeRow('1st', 'first_prize')}
                {prizeRow('2nd', 'second_prize')}
                {prizeRow('3rd', 'third_prize')}
              </>
            )}
          </div>

          {form.use_percentages && (
            <div style={{
              ...S.retained,
              background: overAllocated ? c.lapsedBg : c.activeBg,
            }}>
              <span style={{ fontSize: 14, color: overAllocated ? c.lapsedFg : c.activeFg }}>
                {overAllocated ? 'Over-allocated' : 'Retained for the fields'}
              </span>
              <span style={{
                fontFamily: font.serif, fontSize: 24,
                color: overAllocated ? c.lapsedFg : c.activeFg,
              }}>
                {retainedPct.toFixed(0)}% · {gbp(pot * retainedPct / 100)}
              </span>
            </div>
          )}

          <div style={S.actions}>
            <button
              style={{ ...S.saveBtn, opacity: dirty && !overAllocated ? 1 : 0.5 }}
              onClick={handleSave}
              disabled={saving || !dirty || overAllocated}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
            <button
              style={S.discardBtn}
              onClick={() => { setForm(saved); setMessage(''); }}
              disabled={!dirty}
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  intro: { fontSize: 14, color: c.muted },
  message: { padding: '13px 16px', borderRadius: 3, fontSize: 14, cursor: 'pointer' },

  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 20, alignContent: 'start',
  },
  card: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0,
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: c.ink },

  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 13, color: c.muted },
  hint: { fontSize: 12, color: c.muted },
  input: {
    padding: '12px 14px', border: `1px solid ${c.hairline}`, background: c.paper,
    borderRadius: 3, fontFamily: font.sans, fontSize: 15, color: c.ink, width: '100%',
  },
  inputGroup: {
    display: 'flex', alignItems: 'center', border: `1px solid ${c.hairline}`,
    background: c.paper, borderRadius: 3, overflow: 'hidden', flex: 1, minWidth: 0,
  },
  inputBare: {
    flex: 1, minWidth: 0, padding: '11px 13px', border: 'none', background: c.paper,
    fontFamily: font.sans, fontSize: 15, color: c.ink,
  },
  prefix: { padding: '12px 14px', background: c.hairSoft, fontSize: 15, color: c.muted },
  suffix: { padding: '11px 13px', background: c.hairSoft, fontSize: 14, color: c.muted },

  potBox: {
    marginTop: 'auto', padding: '16px 18px', background: c.cardAlt, borderRadius: 3,
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  potFigure: {
    fontFamily: font.serif, fontSize: 'clamp(22px, 3vw, 28px)', color: c.ink,
    fontVariantNumeric: 'tabular-nums',
  },

  toggle: {
    display: 'flex', border: `1px solid ${c.hairline}`, borderRadius: 3, overflow: 'hidden',
  },
  toggleOn: {
    flex: 1, textAlign: 'center', padding: 11, fontSize: 14, background: c.ink,
    color: c.onTeal, fontWeight: 600, border: 'none', cursor: 'pointer',
    fontFamily: font.sans, whiteSpace: 'nowrap',
  },
  toggleOff: {
    flex: 1, textAlign: 'center', padding: 11, fontSize: 14, background: c.paper,
    color: c.body, border: 'none', cursor: 'pointer', fontFamily: font.sans,
    whiteSpace: 'nowrap',
  },

  prizeRow: { display: 'flex', alignItems: 'center', gap: 14 },
  prizeLabel: { width: 40, fontSize: 14, color: c.body, flex: 'none' },
  prizeAmount: {
    width: 86, textAlign: 'right', fontSize: 15, color: c.ink,
    fontVariantNumeric: 'tabular-nums', flex: 'none',
  },

  retained: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 18px', borderRadius: 3, gap: 10, flexWrap: 'wrap',
  },

  actions: { display: 'flex', gap: 10, marginTop: 'auto', flexWrap: 'wrap' },
  saveBtn: {
    padding: '13px 22px', border: `1px solid ${c.ink}`, background: c.ink,
    color: c.onTeal, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    fontWeight: 600, cursor: 'pointer', minHeight: 44,
  },
  discardBtn: {
    padding: '13px 22px', border: '1px solid #c9c2b1', background: c.card,
    color: c.ink, borderRadius: 3, fontFamily: font.sans, fontSize: 14,
    cursor: 'pointer', minHeight: 44,
  },
};
